/**
 * Transaction History Module - MIGRATED TO REUSABLE WAREHOUSE TABLE
 *
 * This demonstrates the new TransactionHistory implementation using the reusable
 * WarehouseTable system, showing 82% code reduction (987 lines → 150 lines)
 * while maintaining identical functionality and appearance.
 *
 * Features maintained:
 * - Date range filters with API calls
 * - Transaction type badges with color coding
 * - Excel-like column configuration and filtering
 * - Wildcard search patterns with % support
 * - Column sorting, resizing, and reordering
 * - Summary calculations (total quantity)
 * - CSV export functionality
 * - All original styling and behavior preserved
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  History,
  Calendar,
  Filter,
  RefreshCw,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { transactionHistoryService, TransactionFilterOptions } from '../../../../services/transactionHistoryService';
import { useOperatorDisplayNames } from '../../../../hooks/useOperatorDisplayNames';
import { warehouseService } from '../../../../services/warehouseService';
import { statusCodesService } from '../../../../services/statusCodesService';
import { StatusBadge } from '../../../common/StatusBadge';
import { useStatusOptions } from '../../../common/statusPresentation';
import {
  StcTransactionDto
} from '../../../../types/transactionHistory';
import { useAuth } from '../../../../contexts/AuthContext';
import { useTranslation } from '../../../../contexts/I18nProvider';

// Import the new reusable warehouse table system
import { WarehouseTable } from '../../../shared/table';
import { createColumnFactory } from '../../../shared/table/utils/tableUtils';
import type { ColumnDefinition, SummaryCalculation, ServerFetchParams } from '../../../shared/table/types/table';
import { BackButton } from '../../../common/BackButton';

// Create column factory for type-safe column definitions
const columnFactory = createColumnFactory<StcTransactionDto>();

// Helper functions for date handling and formatting
const getDefaultDateRange = () => {
  const toDate = new Date();
  const fromDate = new Date();
  fromDate.setDate(fromDate.getDate() - 7);
  return { fromDate, toDate };
};

const formatDateTimeLocal = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}.${month}.${year} ${hours}:${minutes}`;
};

const parseDateTimeLocal = (dateTimeString: string): Date => {
  // Parse format: "25.11.2025 14:30"
  const [datePart, timePart] = dateTimeString.split(' ');
  const [day, month, year] = datePart.split('.');
  const [hours, minutes] = timePart.split(':');
  return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hours), parseInt(minutes));
};

const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  const dateStr = date.toLocaleDateString('no-NO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
  const timeStr = date.toLocaleTimeString('no-NO', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  return `${dateStr} ${timeStr}`;
};

const formatNumber = (num: number): string => {
  return num.toLocaleString('no-NO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

interface TransactionHistoryAppProps {
  onBack?: () => void;
}

export const TransactionHistoryApp: React.FC<TransactionHistoryAppProps> = ({ onBack }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const defaultDateRange = getDefaultDateRange();

  // B3.1: TransType er StatusCodes-oppslag (StatusType 27). Autoritativ kilde for tekst er
  // StatusCodes (tenant-overstyrbar, dekker alle gyldige koder inkl. f.eks. 17 Internflytt).
  const [transTypeMap, setTransTypeMap] = useState<Map<number, string>>(new Map());

  useEffect(() => {
    statusCodesService.getByStatusType(27)
      .then(codes => setTransTypeMap(new Map(codes.map(sc => [sc.statusCode, sc.lng_1]))))
      .catch(() => setTransTypeMap(new Map()));
  }, []);

  // Språk-bevisste, KODE-baserte filteropsjoner for Typ-kolonnen (StatusType 27).
  // Filterverdi = kode (språknøytral) → backend filtrerer WHERE TransType IN (koder).
  const transTypeOptions = useStatusOptions(27);

  // Transaction-type label by numeric code. Prioritet: StatusCodes (StatusType 27) →
  // app-i18n (flerspråk-fallback) → server-levert fallback for ukjente koder.
  const getTransTypeLabel = useCallback((transType: number, fallback: string): string => {
    const fromStatusCodes = transTypeMap.get(transType);
    if (fromStatusCodes) return fromStatusCodes;

    const i18nLabels: Record<number, string> = {
      10: t('manager.modules.transactionHistory.transTypes.10', {}, 'Plukkliste'),
      11: t('manager.modules.transactionHistory.transTypes.11', {}, 'Lageroverføring'),
      12: t('manager.modules.transactionHistory.transTypes.12', {}, 'Utlegg'),
      13: t('manager.modules.transactionHistory.transTypes.13', {}, 'Påfylling'),
      14: t('manager.modules.transactionHistory.transTypes.14', {}, 'Optimalisering'),
      15: t('manager.modules.transactionHistory.transTypes.15', {}, 'Korreksjon'),
      17: t('manager.modules.transactionHistory.transTypes.17', {}, 'Internflytt'),
      20: t('manager.modules.transactionHistory.transTypes.20', {}, 'Varemottak'),
      88: t('manager.modules.transactionHistory.transTypes.88', {}, 'Inngående saldo'),
      100: t('manager.modules.transactionHistory.transTypes.100', {}, 'Telling')
    };
    return i18nLabels[transType] ?? fallback;
  }, [t, transTypeMap]);

  // State management
  const [transactions, setTransactions] = useState<StcTransactionDto[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true); // Start true for initial load spinner
  const [error, setError] = useState<string | null>(null);

  // Date filter state (for API calls)
  const [fromDate, setFromDate] = useState<Date>(defaultDateRange.fromDate);
  const [toDate, setToDate] = useState<Date>(defaultDateRange.toDate);

  // Pending date changes (for "Velg" button UX)
  const [pendingFromDate, setPendingFromDate] = useState<Date | null>(null);
  const [pendingToDate, setPendingToDate] = useState<Date | null>(null);

  // Server pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const lastFetchRef = useRef<{
    page: number;
    size: number;
    sortColumn?: string;
    sortDirection?: string;
    filters?: Record<string, string>;
    dropdownFilters?: Record<string, string[]>;
  }>({ page: 1, size: 50 });
  const filterDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialLoadDoneRef = useRef(false);
  const fetchIdRef = useRef(0); // Monotonic counter to discard stale API responses

  // Dropdown filter options from all data in date range (not just current page)
  const [filterOptions, setFilterOptions] = useState<TransactionFilterOptions | null>(null);

  // Lager-navn (kode → beskrivelse) for B3 «kode - tekst»-visning på Lager-kolonnen
  const [warehouseMap, setWarehouseMap] = useState<Map<string, string>>(new Map());

  // Operatørnavn (UserID → ProfileName) — kunden skal se navn, ikke ID (TW 36353248).
  // Verdien i raden er fortsatt UserID, så sortering og server-filtrering er uberørt.
  const { getDisplayName } = useOperatorDisplayNames();

  useEffect(() => {
    warehouseService.getWarehouses()
      .then(list => setWarehouseMap(new Map(list.map(w => [String(w.warehouseID), w.warehouseDescr]))))
      .catch(() => setWarehouseMap(new Map()));
  }, []);

  /**
   * Define columns using the type-safe column factory
   */
  const columns: ColumnDefinition<StcTransactionDto>[] = useMemo(() => [
    columnFactory.createDateColumn('transDate', 'transDate', t('common.fields.date', {}, 'Dato'), {
      width: 140,
      sortable: true,
      filterType: 'none', // No filter - handled by date range picker
      formatter: (value: string) => formatDateTime(value)
    }),
    columnFactory.createTextColumn('warehouseID', 'warehouseID', t('warehouse.general.warehouse', {}, 'Lager'), {
      width: 150,
      sortable: true,
      filterType: 'both', // oppslagstabell — flervalgs-dropdown (verdier fra filterOptions)
      // B3: vis «kode - navn» (filtrering skjer fortsatt på koden)
      formatter: (value: any) => {
        if (value === null || value === undefined || value === '') return String(value || '-');
        const name = warehouseMap.get(String(value));
        return name ? `${value} - ${name}` : String(value);
      }
    }),
    columnFactory.createTextColumn('binCode', 'binCode', t('warehouse.general.bin', {}, 'Lokasjon'), {
      width: 100,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createCustomColumn({
      id: 'transTypeDescription',
      key: 'transTypeDescription',
      label: t('common.fields.type', {}, 'Type'),
      width: 140,
      visible: true,
      sortable: true,
      resizable: true,
      minWidth: 100,
      alignment: 'left',
      type: 'text',
      filterType: 'dropdown',
      // Kode-basert, språk-bevisst filter (filterOptions vinner over kildeliste). Verdi = kode.
      filterOptions: transTypeOptions.map(o => ({ value: String(o.value), label: o.label })),
      // B3.1: Type er StatusCodes-oppslag (StatusType 27). «kode - tekst» i dropdown/eksport via
      // formatter. Returner '' for tomme/syntetiske rader (transType 0) så de IKKE blir en «-»-verdi
      // i Type-dropdownen (de syntetiske Lager-radene deler datasett og har tom transTypeDescription).
      formatter: (value: any, row: StcTransactionDto) =>
        row.transType != null && row.transType !== 0
          ? `${row.transType} - ${getTransTypeLabel(row.transType, String(value || ''))}`
          : '',
      // Enhetlig visning via StatusBadge (StatusType 27, språk-bevisst «kode - tekst» + farge).
      // Filter/eksport bruker fortsatt getTransTypeLabel (formatter over).
      renderer: (_value: string, row: StcTransactionDto) =>
        row.transType != null && row.transType !== 0
          ? <StatusBadge value={row.transType} statusType={27} />
          : <span className="text-gray-400">-</span>
    }),
    columnFactory.createTextColumn('itemNo', 'itemNo', t('manager.modules.transactionHistory.columns.itemNo', {}, 'Varenr.'), {
      width: 120,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('itemDescription', 'itemDescription', t('manager.modules.transactionHistory.columns.itemDescription', {}, 'Varebeskrivelse'), {
      width: 200,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createNumberColumn('quantity', 'quantity', t('common.fields.quantity', {}, 'Antall'), {
      width: 80,
      sortable: true,
      filterType: 'text',
      formatter: (value: number) => formatNumber(value)
    }),
    columnFactory.createTextColumn('operatorID', 'operatorID', t('manager.modules.transactionHistory.columns.operator', {}, 'Operatør'), {
      width: 150,
      sortable: true,
      // «both» gir både fritekst på koden og flervalgs-dropdown fra filterOptions.
      // Uten dropdownen ville et søk på navnet ikke truffet noe, siden server-filteret
      // matcher operatorID. Dropdownen viser navnet (via formatteren) og sender koden.
      filterType: 'both',
      formatter: (value: any) => {
        if (value === null || value === undefined || value === '') return '-';
        return getDisplayName(String(value));
      }
    }),
    columnFactory.createTextColumn('serialNo', 'serialNo', t('manager.modules.transactionHistory.columns.serialNo', {}, 'Serienr.'), {
      width: 110,
      sortable: true,
      filterType: 'text' // frie verdier rett på StcTransaction — ingen lookup-tabell
    }),
    columnFactory.createTextColumn('batchNo', 'batchNo', t('manager.modules.transactionHistory.columns.batchNo', {}, 'Batchnr.'), {
      width: 100,
      sortable: true,
      filterType: 'text' // frie verdier rett på StcTransaction — ingen lookup-tabell
    }),
    columnFactory.createDateColumn('expiryDate', 'expiryDate', t('manager.modules.transactionHistory.columns.expiryDate', {}, 'Utløpsdato'), {
      width: 100,
      sortable: true,
      filterType: 'both'
    }),
    columnFactory.createTextColumn('orderNoERP', 'orderNoERP', t('manager.modules.transactionHistory.columns.orderNoERP', {}, 'Ordre (ERP)'), {
      width: 100,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('projectNo', 'projectNo', t('manager.modules.transactionHistory.columns.projectNo', {}, 'Prosjektnr'), {
      width: 110,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('depNo', 'depNo', t('manager.modules.transactionHistory.columns.depNo', {}, 'Avdeling'), {
      width: 100,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('lineNoERP', 'lineNoERP', t('manager.modules.transactionHistory.columns.lineNoERP', {}, 'Linje (ERP)'), {
      width: 90,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('orderName', 'orderName', t('manager.modules.transactionHistory.columns.orderName', {}, 'Kunde/Lev.'), {
      width: 150,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('pListID', 'pListID', t('manager.modules.transactionHistory.columns.pListID', {}, 'Plukkliste'), {
      width: 90,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('pListLineNo', 'pListLineNo', t('manager.modules.transactionHistory.columns.pListLineNo', {}, 'Plukklinje'), {
      width: 90,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('grShipmentNo', 'grShipmentNo', t('manager.modules.transactionHistory.columns.grShipmentNo', {}, 'Partinr.'), {
      width: 80,
      sortable: true,
      filterType: 'text'
    }),
    columnFactory.createTextColumn('reasonDescription', 'reasonDescription', t('manager.modules.transactionHistory.columns.reasonDescription', {}, 'Årsak'), {
      width: 150,
      sortable: true,
      filterType: 'text'
    })
    // getDisplayName MÅ være med i avhengighetslista under: profillista lastes
    // asynkront, og uten den ville kolonnene blitt memoisert med et tomt
    // navneoppslag og fortsatt vist operatør-ID etter at navnene kom inn.
  ], [t, getTransTypeLabel, warehouseMap, transTypeOptions, getDisplayName]);

  /**
   * Define summary calculations
   */
  const summaryCalculations: SummaryCalculation<StcTransactionDto>[] = useMemo(() => [
    {
      column: 'quantity',
      type: 'sum',
      formatter: (value: number) =>
        t('manager.modules.transactionHistory.summary.total', { '0': formatNumber(value) }, `Total: ${formatNumber(value)}`)
    }
  ], [t]);

  // Fetch all distinct filter values for dropdowns (across entire date range)
  const loadFilterOptions = useCallback(async () => {
    try {
      const options = await transactionHistoryService.getFilterOptions(fromDate, toDate);
      setFilterOptions(options);
    } catch (err) {
      console.error('Failed to load filter options:', err);
    }
  }, [fromDate, toDate]);

  // Build synthetic data rows so ColumnDropdownFilter shows ALL distinct values (across hele
  // datointervallet, ikke bare gjeldende side). Hver rad bærer verdien for én dropdown-kolonne;
  // tomme felt hoppes over per kolonne (formattedValue.trim() === '' filtreres bort).
  const dropdownOptionsData: StcTransactionDto[] = useMemo(() => {
    const emptyRow = {
      id: -1, companyNo: '', warehouseID: '', transDate: '', transType: 0,
      transTypeDescription: '', itemNo: '', itemDescription: '', binCode: '',
      quantity: 0, operatorID: '', orderNo: null, orderNoERP: null, projectNo: null,
      depNo: null, lineNo: null, lineNoERP: null, orderName: null, pListID: null,
      pListLineNo: null, grShipmentNo: null, reasonDescription: null, clientID: null,
      created: null, createdBy: null,
    } as unknown as StcTransactionDto;

    // Type-rader fra StatusCodes (StatusType 27) — dekker alle gyldige typer i kildelista.
    // Filteret er kode-basert (eksplisitt filterOptions vinner, verdi=kode; backend matcher
    // st.TransType IN (koder)); transTypeDescription her er kun kildeliste-tekst, formatteren viser «kode - tekst».
    const typeRows = Array.from(transTypeMap.entries()).map(([code, lng]) => ({
      ...emptyRow,
      transType: code,
      transTypeDescription: lng,
    } as StcTransactionDto));

    const warehouseRows = (filterOptions?.warehouseIds || []).map(wid => ({
      ...emptyRow,
      warehouseID: wid,
    } as StcTransactionDto));

    // Operatør-rader gir dropdownen en komplett liste over operatørene i perioden.
    // Verdien er UserID; formatteren gjør den om til ProfileName i visningen.
    const operatorRows = (filterOptions?.operatorIds || []).map(oid => ({
      ...emptyRow,
      operatorID: oid,
    } as StcTransactionDto));

    return [...typeRows, ...warehouseRows, ...operatorRows];
  }, [transTypeMap, filterOptions]);

  // Load transactions with server-side pagination, sorting and filtering
  const loadTransactions = useCallback(async (
    page: number = currentPage,
    size: number = pageSize,
    sortColumn?: string,
    sortDirection?: string,
    filters?: Record<string, string>,
    dropdownFilters?: Record<string, string[]>
  ) => {
    // Only show full loading spinner on initial load — subsequent fetches keep table visible
    // (same pattern as ItemsDataTable)
    if (!initialLoadDoneRef.current) {
      setLoading(true);
    }
    setError(null);

    // Increment request counter — if a newer request starts before this one finishes,
    // discard this response to prevent stale data from overwriting fresher results
    const thisRequestId = ++fetchIdRef.current;

    try {
      // Send column filters and dropdown filters to service — it handles key mapping to API params
      const result = await transactionHistoryService.getTransactionHistoryPaged(
        fromDate,
        toDate,
        page,
        size,
        filters,
        dropdownFilters,
        sortColumn,
        sortDirection
      );

      // Discard stale response — a newer request has been issued since this one started
      if (thisRequestId !== fetchIdRef.current) {
        return;
      }

      setTransactions(result.items);
      setTotalRecords(result.totalRecords);
      setCurrentPage(page);
      setPageSize(size);
      lastFetchRef.current = { page, size, sortColumn, sortDirection, filters, dropdownFilters };
      initialLoadDoneRef.current = true;
    } catch (err) {
      // Only show error if this is still the latest request
      if (thisRequestId === fetchIdRef.current) {
        setError(err instanceof Error ? err.message : t('manager.modules.transactionHistory.errors.unknown', {}, 'En ukjent feil oppstod'));
      }
    } finally {
      if (thisRequestId === fetchIdRef.current) {
        setLoading(false);
      }
    }
  }, [fromDate, toDate, currentPage, pageSize, t]);

  // Handle server pagination/sort/filter callback from WarehouseTable
  // Filter changes are debounced 300ms to prevent flickering while typing
  const handleFetchData = useCallback((params: ServerFetchParams) => {
    const { pageNumber, pageSize: newSize, sortColumn, sortDirection, filters, dropdownFilters } = params;
    const last = lastFetchRef.current;

    // Avoid duplicate fetches for identical params
    const isSame = pageNumber === last.page
      && newSize === last.size
      && sortColumn === last.sortColumn
      && sortDirection === last.sortDirection
      && JSON.stringify(filters) === JSON.stringify(last.filters)
      && JSON.stringify(dropdownFilters) === JSON.stringify(last.dropdownFilters);

    if (isSame) {
      return;
    }

    // Clear any pending debounce
    if (filterDebounceRef.current) {
      clearTimeout(filterDebounceRef.current);
      filterDebounceRef.current = null;
    }

    // Detect if this is a text filter change (debounce to avoid flicker while typing)
    const isTextFilterChange = pageNumber === 1
      && JSON.stringify(filters) !== JSON.stringify(last.filters);

    // Dropdown filter changes should execute immediately (discrete selection, not typing)
    const isDropdownChange = JSON.stringify(dropdownFilters) !== JSON.stringify(last.dropdownFilters);

    if (isTextFilterChange && !isDropdownChange && initialLoadDoneRef.current) {
      // Debounce text filter changes only
      filterDebounceRef.current = setTimeout(() => {
        loadTransactions(pageNumber, newSize, sortColumn, sortDirection, filters, dropdownFilters);
      }, 300);
    } else {
      // Page, sort, dropdown changes execute immediately
      loadTransactions(pageNumber, newSize, sortColumn, sortDirection, filters, dropdownFilters);
    }
  }, [loadTransactions]);

  // Load on mount
  useEffect(() => {
    loadTransactions(1, 50);
    loadFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Date filter handlers
  const handleApplyFilters = () => {
    const last = lastFetchRef.current;
    loadTransactions(1, pageSize, last.sortColumn, last.sortDirection, last.filters, last.dropdownFilters);
    loadFilterOptions(); // Refresh dropdown options for new date range
  };

  const handleResetFilters = () => {
    const defaultRange = getDefaultDateRange();
    setFromDate(defaultRange.fromDate);
    setToDate(defaultRange.toDate);
    setPendingFromDate(null);
    setPendingToDate(null);
  };

  const handleFromDateChange = (dateString: string) => {
    try {
      setPendingFromDate(parseDateTimeLocal(dateString));
    } catch {
      // Invalid format, ignore
    }
  };

  const handleToDateChange = (dateString: string) => {
    try {
      setPendingToDate(parseDateTimeLocal(dateString));
    } catch {
      // Invalid format, ignore
    }
  };

  const handleConfirmFromDate = () => {
    if (pendingFromDate) {
      setFromDate(pendingFromDate);
      setPendingFromDate(null);
    }
  };

  const handleConfirmToDate = () => {
    if (pendingToDate) {
      setToDate(pendingToDate);
      setPendingToDate(null);
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden bg-gray-50">
      {/* Header with integrated filters */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 sticky top-0 z-10 shadow-sm">
        <div className="flex items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <BackButton onClick={onBack || (() => {})} />
            <History className="w-5 h-5 text-teal-600" />
            <h1 className="text-lg font-semibold text-gray-900">{t('manager.modules.transactionHistory.title', {}, 'Transaksjonshistorikk')}</h1>
            {loading && <Loader2 className="w-4 h-4 text-teal-600 animate-spin" />}
          </div>
          
          {/* Date Range Filters */}
          <div className="flex items-center gap-4">
          {/* From Date */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 flex items-center gap-1 whitespace-nowrap">
              <Calendar className="w-4 h-4" />
              {t('manager.modules.transactionHistory.filters.fromDate', {}, 'Fra dato')}
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder={t('manager.modules.transactionHistory.filters.datePlaceholder', {}, 'dd.mm.åååå hh:mm')}
                value={formatDateTimeLocal(pendingFromDate || fromDate)}
                onChange={e => handleFromDateChange(e.target.value)}
                className={`w-44 px-3 py-2 pr-8 border rounded-lg focus:outline-none focus:ring-2 text-sm transition-colors ${
                  pendingFromDate
                    ? 'border-teal-400 ring-2 ring-teal-200 bg-teal-50'
                    : 'border-gray-300 focus:ring-teal-500'
                }`}
              />
              <input
                type="date"
                value={formatDateTimeLocal(pendingFromDate || fromDate).split(' ')[0].split('.').reverse().join('-')}
                onChange={e => {
                  const selectedDate = new Date(e.target.value + 'T00:00:00');
                  const currentTime = pendingFromDate || fromDate;
                  selectedDate.setHours(currentTime.getHours(), currentTime.getMinutes());
                  setPendingFromDate(selectedDate);
                }}
                className="absolute right-1 top-1/2 transform -translate-y-1/2 w-6 h-6 opacity-0 cursor-pointer"
                title={t('manager.modules.transactionHistory.aria.openDatePicker', {}, 'Velg dato med datovelger')}
              />
              <Calendar className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
            {pendingFromDate && (
              <button
                onClick={handleConfirmFromDate}
                className="px-3 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors shadow-sm animate-fadeIn"
                title={t('manager.modules.transactionHistory.aria.confirmDate', {}, 'Bekreft valgt dato')}
              >
                {t('manager.modules.transactionHistory.buttons.select', {}, 'Velg')}
              </button>
            )}
          </div>

          {/* To Date */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 flex items-center gap-1 whitespace-nowrap">
              <Calendar className="w-4 h-4" />
              {t('manager.modules.transactionHistory.filters.toDate', {}, 'Til dato')}
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder={t('manager.modules.transactionHistory.filters.datePlaceholder', {}, 'dd.mm.åååå hh:mm')}
                value={formatDateTimeLocal(pendingToDate || toDate)}
                onChange={e => handleToDateChange(e.target.value)}
                className={`w-44 px-3 py-2 pr-8 border rounded-lg focus:outline-none focus:ring-2 text-sm transition-colors ${
                  pendingToDate
                    ? 'border-teal-400 ring-2 ring-teal-200 bg-teal-50'
                    : 'border-gray-300 focus:ring-teal-500'
                }`}
              />
              <input
                type="date"
                value={formatDateTimeLocal(pendingToDate || toDate).split(' ')[0].split('.').reverse().join('-')}
                onChange={e => {
                  const selectedDate = new Date(e.target.value + 'T00:00:00');
                  const currentTime = pendingToDate || toDate;
                  selectedDate.setHours(currentTime.getHours(), currentTime.getMinutes());
                  setPendingToDate(selectedDate);
                }}
                className="absolute right-1 top-1/2 transform -translate-y-1/2 w-6 h-6 opacity-0 cursor-pointer"
                title={t('manager.modules.transactionHistory.aria.openDatePicker', {}, 'Velg dato med datovelger')}
              />
              <Calendar className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
            {pendingToDate && (
              <button
                onClick={handleConfirmToDate}
                className="px-3 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors shadow-sm animate-fadeIn"
                title={t('manager.modules.transactionHistory.aria.confirmDate', {}, 'Bekreft valgt dato')}
              >
                {t('manager.modules.transactionHistory.buttons.select', {}, 'Velg')}
              </button>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleApplyFilters}
              disabled={loading}
              className="flex items-center gap-2 px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Filter className="w-4 h-4" />
              {t('manager.modules.transactionHistory.buttons.fetchData', {}, 'Hent data')}
            </button>
            <button
              onClick={handleResetFilters}
              disabled={loading}
              className="flex items-center gap-2 px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className="w-4 h-4" />
              {t('manager.modules.transactionHistory.buttons.reset', {}, 'Tilbakestill')}
            </button>
          </div>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="m-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800">{t('manager.modules.transactionHistory.errors.loadFailed', {}, 'Feil ved lasting av data')}</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
              <button
                onClick={() => loadTransactions(currentPage, pageSize)}
                className="mt-3 px-4 py-2 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
              >
                {t('manager.modules.transactionHistory.buttons.retry', {}, 'Prøv igjen')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content - WarehouseTable */}
      <div className="flex-1 min-h-0">
        <WarehouseTable
          data={transactions}
          columns={columns}
          config={{
            showPagination: true,
            showColumnConfig: true,
            showGlobalSearch: false, // Date filtering handled separately
            showFilters: true,
            showExport: true,
            showPrint: true,
            showSummary: true,
            defaultPageSize: 50,
            allowColumnReorder: true,
            allowColumnResize: true,
            stickyHeader: true,
            compactMode: true,
            persistenceKey: 'transaction-history'
          }}
          serverPagination={{
            enabled: true,
            totalRecords: totalRecords,
            onFetchData: handleFetchData
          }}
          dropdownOptionsData={dropdownOptionsData.length > 0 ? dropdownOptionsData : undefined}
          enableDropdownFilters={true}
          summaryCalculations={summaryCalculations}
          loading={loading}
          error={error || undefined}
        />
      </div>
    </div>
  );
};